import { Building2, CircleUserRound, FileSignature, Info } from 'lucide-vue-next'
import CalendarFormatted from 'pejer/src/components/CalendarFormatted.vue'
import { Groups, JSSchema } from 'pejer/src/tables'
import { ColorPalette } from 'pejer/src/tables/helpers'
import { h } from 'vue'

export namespace PersonsOfInterestEmbeds {
  export interface Full extends ListItem {}

  export interface ListItem {
    id: number
    countryCode: string
    organizationId: string
    businessName: string
    platformName: string
    listSource: string
    name: string
    title: string
    signatureRequired: string
    signedAt: string
    signatureEmail: string
    signatureUrl: string
    lastNotifiedAt: string
    titleList: string[] | null
  }
}

export const PersonsOfInterestGroups = {
  Personlig: {
    color: ColorPalette.green,
    icon: CircleUserRound,
    isMainGroup: true,
    mainColumn: 'name',
  },
  Business: {
    color: ColorPalette.blue,
    icon: Building2,
    mainColumn: 'businessName',
  },
  Signatur: {
    color: ColorPalette.yellow,
    icon: FileSignature,
    mainColumn: 'signedAt',
  },
  Annet: {
    color: ColorPalette.brown,
    icon: Info,
    mainColumn: 'id',
  },
} satisfies Groups

export const PersonsOfInterestEmbedListSchema: JSSchema<
  PersonsOfInterestEmbeds.ListItem,
  typeof PersonsOfInterestGroups
> = {
  name: 'Persons Of Interest Embed',
  title: 'Persons of Interest',
  groups: PersonsOfInterestGroups,
  fields: {
    countryCode: {
      type: 'string',
      humanReadable: 'Landkode',
      meta: {
        group: 'Business',
      },
      metaType: 'countryCode',
    },
    organizationId: {
      type: 'string',
      humanReadable: 'Organisasjonsnummer',
      meta: {
        group: 'Business',
      },
      sensitive: true,
    },
    businessName: {
      type: 'string',
      humanReadable: 'Bedriftsnavn',
      meta: {
        group: 'Business',
      },
    },
    platformName: {
      type: 'string',
      humanReadable: 'Plattformnavn',
      meta: {
        group: 'Business',
      },
    },
    listSource: {
      type: 'string',
      humanReadable: 'Kilde',
      meta: {
        group: 'Personlig',
      },
    },
    name: {
      type: 'string',
      humanReadable: 'Navn',
      meta: {
        group: 'Personlig',
      },
      sensitive: true,
      inSearchSelect: true,
    },
    // title: {
    //   type: 'string',
    //   humanReadable: 'Tittel',
    //   meta: {
    //     group: 'Personlig',
    //   },
    // },
    titleList: {
      type: 'string',
      humanReadable: 'Company titles',
      meta: {
        group: 'Personlig',
      },
      metaType: 'array',
    },
    signatureRequired: {
      type: 'date',
      humanReadable: 'Signatur påkrevd',
      meta: {
        group: 'Signatur',
      },
    },
    signedAt: {
      type: 'date',
      humanReadable: 'Signatur dato',
      meta: {
        group: 'Signatur',
      },
      specificCell:
        () =>
        ({ row }) => {
          if (!row.original.signatureRequired)
            return h('span', { class: 'text-slate-500 italic' }, 'Signering ikke påkrevd')

          if (!row.original.signedAt)
            return h('span', { class: 'text-red-500' }, 'Mangler signatur')

          return h('div', { class: 'text-green-500' }, [
            'Signert: ',
            h(CalendarFormatted, { date: row.original.signedAt }),
          ])
        },
    },
    signatureEmail: {
      type: 'string',
      humanReadable: 'Signatur e-post',
      meta: {
        group: 'Signatur',
      },
      sensitive: true,
    },
    signatureUrl: {
      type: 'string',
      humanReadable: 'Signatur URL',
      meta: {
        group: 'Signatur',
      },
      metaType: 'url',
      sensitive: true,
    },
    lastNotifiedAt: {
      type: 'date',
      humanReadable: 'Sist varslet',
      meta: {
        group: 'Signatur',
      },
    },
    id: {
      type: 'number',
      humanReadable: 'ID',
      meta: {
        group: 'Annet',
      },
      isDefaultSortColumn: true,
    },
  },
}

// Warning list schema is the same as full schema, but if this changes in the future, we need to change it here
export const PersonsOfInterestEmbedFullSchema: JSSchema<
  PersonsOfInterestEmbeds.Full,
  typeof PersonsOfInterestGroups
> = {
  ...PersonsOfInterestEmbedListSchema,
  title: 'Person of Interest',
}
